// App readiness helpers.
//
// The shell (public/index.html) paints a boot splash before the React bundle
// is even parsed. These helpers tell the app when it is safe to take that
// splash down: the page is only "ready" once the window has finished loading,
// the site config has arrived (every image src is built from
// window.site.common.imageCloudfront, so before it lands every <img> is
// broken), the icon fonts are in (until then Material ligatures paint as their
// raw names - "grid_view", "arrow_back" ...) and the images the first screen
// asked for have settled.
//
// Every wait is capped: a slow or dead CDN must never leave the user stuck on
// the splash, so each helper resolves true when the thing happened and false
// when it timed out, and the caller carries on either way.

export const BOOT_SPLASH_ID = "app-boot-splash";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Resolves as soon as `predicate()` is truthy, or false once `timeout` passes.
export function waitFor(predicate, { timeout = 8000, interval = 60 } = {}) {
  const test = () => {
    try {
      return !!predicate();
    } catch {
      return false;
    }
  };

  if (test()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(cap);
      resolve(value);
    };
    const poll = setInterval(() => {
      if (test()) finish(true);
    }, interval);
    const cap = setTimeout(() => finish(false), timeout);
  });
}

// document.readyState === "complete": stylesheets, scripts and the markup's
// own assets are done.
export function waitForWindowLoad({ timeout = 8000 } = {}) {
  if (document.readyState === "complete") return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("load", onLoad);
      clearTimeout(cap);
      resolve(value);
    };
    const onLoad = () => finish(true);
    window.addEventListener("load", onLoad);
    const cap = setTimeout(() => finish(false), timeout);
  });
}

// index.html fetches /config into window.site on boot; imageCloudfront is the
// base URL every logo, icon and placeholder in the app is built from.
export function waitForSiteConfig({ timeout = 8000 } = {}) {
  return waitFor(() => window.site && window.site.common && window.site.common.imageCloudfront, {
    timeout,
  });
}

// The shell's inline script owns the actual font watching (it also unhides the
// icons on its own timeout, so icons can never stay invisible); here we just
// wait for the flag it sets.
export function waitForIconFonts({ timeout = 8000 } = {}) {
  return waitFor(() => window.__ICON_FONTS_READY__, { timeout });
}

// Images the freshly mounted screen kicked off. Errors count as settled - a
// 404 resolves the wait rather than holding the splash for the full cap.
export function waitForImages({ timeout = 4000 } = {}) {
  const pending = Array.from(document.images || []).filter((img) => img.src && !img.complete);
  if (pending.length === 0) return Promise.resolve(true);

  const settled = pending.map(
    (img) =>
      new Promise((resolve) => {
        const done = () => {
          img.removeEventListener("load", done);
          img.removeEventListener("error", done);
          resolve();
        };
        img.addEventListener("load", done);
        img.addEventListener("error", done);
      })
  );

  return Promise.race([
    Promise.all(settled).then(() => true),
    delay(timeout).then(() => false),
  ]);
}

// Two frames: one for React to commit the tree, one for the browser to paint
// it, so the screen underneath the splash is drawn before we fade it out.
export function afterNextPaint() {
  return new Promise((resolve) => {
    if (typeof window.requestAnimationFrame !== "function") {
      setTimeout(resolve, 32);
      return;
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });
}

// Fades the shell's splash out and drops it from the DOM.
export function hideBootSplash() {
  const splash = document.getElementById(BOOT_SPLASH_ID);
  if (!splash) return;

  splash.classList.add("is-hidden");
  const remove = () => splash.remove();
  splash.addEventListener("transitionend", remove, { once: true });
  // transitionend never fires if the element is already display:none-ish.
  setTimeout(remove, 600);
}
